/**
 * 職涯測驗 第一部分's dream questions (理想的一天, 夢想, 年期, 3 年後的你) — shared
 * by the candidate's page and the adviser's RecruitQuizDialog, which shows the
 * answers under 「他想要的生活」. Submitted raw as `idealDay`, `dream`,
 * `horizon`, `identity`.
 */
import { L } from '../CoverQuiz/quizLang';
import type { Txt } from '../CoverQuiz/quizLang';

// 你理想的一天 — pick two scenes.
export const IDEAL_DAY_OPTIONS = [
  { key: 'travel', icon: '🏖️', label: L('平日也能去旅行', '平日也能去旅行', 'Travel on weekdays too') },
  { key: 'kids', icon: '🎒', label: L('接小朋友放學', '接小朋友放学', 'Pick the kids up from school') },
  { key: 'celebrate', icon: '🥂', label: L('和團隊慶祝成交', '和团队庆祝成交', 'Celebrate a deal with the team') },
  { key: 'ownTime', icon: '🗓️', label: L('自己安排時間見客', '自己安排时间见客', 'Set my own client meetings') },
  { key: 'learn', icon: '📚', label: L('下午上課進修', '下午上课进修', 'Take a class in the afternoon') },
  { key: 'family', icon: '🍲', label: L('每晚回家吃飯', '每晚回家吃饭', 'Home for dinner every night') }
];

// 如果收入不再是問題，你最想做什麼？
export const DREAM_OPTIONS = [
  { key: 'familyTrip', icon: '✈️', label: L('帶家人去旅行', '带家人去旅行', 'Take my family travelling') },
  { key: 'home', icon: '🏠', label: L('買自己的樓', '买自己的楼', 'Buy my own home') },
  { key: 'retire', icon: '🌅', label: L('提早退休', '提早退休', 'Retire early') },
  { key: 'parents', icon: '❤️', label: L('照顧父母', '照顾父母', 'Look after my parents') },
  {
    key: 'ownBoss',
    icon: '🚀',
    label: L('自己作主，做自己的事業', '自己作主，做自己的事业', 'Be my own boss, with my own business')
  }
];

// 想幾年內做到？ — asked with the target income.
export const HORIZON_OPTIONS = [
  { value: 1, label: L('1 年內', '1 年内', 'Within 1 year') },
  { value: 3, label: L('3 年內', '3 年内', 'Within 3 years') },
  { value: 5, label: L('5 年內', '5 年内', 'Within 5 years') }
];

// 3 年後，你想別人怎樣形容你？
export const IDENTITY_OPTIONS = [
  { key: 'leader', icon: '👥', label: L('帶領團隊的領袖', '带领团队的领袖', 'A leader with a team') },
  { key: 'mdrt', icon: '🏅', label: L('頂尖顧問（MDRT）', '顶尖顾问（MDRT）', 'A top adviser (MDRT)') },
  { key: 'boss', icon: '🚀', label: L('自己作主的老闆', '自己作主的老板', 'My own boss') },
  { key: 'expert', icon: '🎓', label: L('專業的理財顧問', '专业的理财顾问', 'A professional financial adviser') }
];

type Labelled = { icon?: string; label: Txt };
const labelOf = (options: ReadonlyArray<Labelled & { key: string }>, key: unknown) =>
  options.find(option => option.key === key) || null;

/** The dream answers as labels, skipping anything missing or unknown. */
export const careerDreamLabels = (answers: {
  idealDay?: unknown;
  dream?: unknown;
  horizon?: unknown;
  identity?: unknown;
}) => ({
  idealDay: (Array.isArray(answers.idealDay) ? answers.idealDay : [])
    .map(key => labelOf(IDEAL_DAY_OPTIONS, key))
    .filter((o): o is Labelled & { key: string } => Boolean(o)),
  dream: labelOf(DREAM_OPTIONS, answers.dream),
  horizon: HORIZON_OPTIONS.find(option => option.value === answers.horizon) || null,
  identity: labelOf(IDENTITY_OPTIONS, answers.identity)
});
